/**
 * TaskFeedback - Rate and review completed task results
 * Feeds into auto-improvement system
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ThumbsUp, ThumbsDown, Star, MessageSquare, 
  Send, CheckCircle, X, Loader2 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TaskFeedback({ taskId, templateId, token, onClose }) {
  const [step, setStep] = useState('quick'); // quick, detailed, submitted
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedbackType, setFeedbackType] = useState(null); // thumbs_up, thumbs_down
  const [comment, setComment] = useState('');
  const [whatWentWrong, setWhatWentWrong] = useState('');
  const [expectedResult, setExpectedResult] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleQuickFeedback = async (type) => {
    setFeedbackType(type);
    
    if (type === 'thumbs_up') {
      // Quick positive feedback - submit immediately
      await submitFeedback({
        rating: 5,
        feedback_type: 'thumbs_up'
      });
    } else {
      // Negative - ask for details
      setStep('detailed');
    }
  };

  const submitFeedback = async (feedbackData) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback/submit`, {
        task_id: taskId,
        template_id: templateId,
        rating: feedbackData.rating || rating,
        feedback_type: feedbackData.feedback_type || 'detailed',
        comment: feedbackData.comment || comment,
        what_went_wrong: feedbackData.what_went_wrong || whatWentWrong,
        expected_result: feedbackData.expected_result || expectedResult
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSubmitted(true);
      setStep('submitted');
      
      // Auto close after 2 seconds
      setTimeout(() => {
        if (onClose) onClose();
      }, 2000);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDetailedSubmit = () => {
    submitFeedback({
      rating,
      feedback_type: 'detailed',
      comment,
      what_went_wrong: whatWentWrong,
      expected_result: expectedResult
    });
  };

  // Submitted state
  if (step === 'submitted') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-green-500/10 to-teal-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3"
      >
        <CheckCircle className="w-5 h-5 text-green-400" />
        <span className="text-green-300">Thanks for your feedback! We use this to improve.</span>
      </motion.div>
    );
  }

  // Detailed feedback form
  if (step === 'detailed') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-5"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">Help us improve</h3>
          <button 
            onClick={() => setStep('quick')}
            className="text-zinc-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Star Rating */}
        <div className="mb-4">
          <label className="text-zinc-400 text-sm mb-2 block">How would you rate this result?</label>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                onClick={() => setRating(star)}
                className="p-1 transition-transform hover:scale-110"
              >
                <Star 
                  className={`w-7 h-7 transition-colors ${
                    star <= (hoverRating || rating) 
                      ? 'text-yellow-400 fill-yellow-400' 
                      : 'text-zinc-600'
                  }`} 
                />
              </button>
            ))}
          </div>
        </div>

        {/* What went wrong */}
        <div className="mb-4">
          <label className="text-zinc-400 text-sm mb-2 block">What went wrong?</label>
          <Textarea
            value={whatWentWrong}
            onChange={(e) => setWhatWentWrong(e.target.value)}
            placeholder="e.g., The output was too generic, missing key details..."
            className="bg-zinc-900 border-zinc-700 text-white min-h-[80px]"
          />
        </div>

        {/* What did you expect */}
        <div className="mb-4">
          <label className="text-zinc-400 text-sm mb-2 block">What did you expect instead?</label>
          <Textarea
            value={expectedResult}
            onChange={(e) => setExpectedResult(e.target.value)}
            placeholder="e.g., I wanted more specific examples, better formatting..."
            className="bg-zinc-900 border-zinc-700 text-white min-h-[80px]"
          />
        </div>

        {/* Additional comments */}
        <div className="mb-4">
          <label className="text-zinc-400 text-sm mb-2 block">Any other comments? (optional)</label>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Additional feedback..."
            className="bg-zinc-900 border-zinc-700 text-white min-h-[60px]"
          />
        </div>

        <Button
          onClick={handleDetailedSubmit}
          disabled={submitting || rating === 0}
          className="w-full bg-teal-500 hover:bg-teal-600"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Submit Feedback
            </>
          )}
        </Button>
      </motion.div>
    );
  }

  // Quick feedback (default)
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-4"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <span className="text-zinc-400 text-sm">Was this result helpful?</span>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleQuickFeedback('thumbs_up')}
            disabled={submitting}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 hover:bg-green-500/20 transition-colors"
            data-testid="feedback-thumbs-up"
          >
            <ThumbsUp className="w-4 h-4" />
            <span className="text-sm">Yes</span>
          </button>
          <button
            onClick={() => handleQuickFeedback('thumbs_down')}
            disabled={submitting}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors"
            data-testid="feedback-thumbs-down"
          >
            <ThumbsDown className="w-4 h-4" />
            <span className="text-sm">No</span>
          </button>
          <button
            onClick={() => setStep('detailed')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-700/50 text-zinc-400 hover:bg-zinc-700 transition-colors"
            data-testid="feedback-detailed"
          >
            <MessageSquare className="w-4 h-4" />
            <span className="text-sm">Details</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
}
